/**
 * CreateProjectFromPaymentPanel — AD-031 Increment C.3 (Anil).
 *
 * The MANUAL-confirm, AUTO-SUGGESTED "Create project from this payment" action.
 * Shown when staff are recording a payment for a client who has no delivery
 * project yet (e.g. paid off a quote / scoping). It pre-fills client + service +
 * amount; staff confirm; on confirm it creates the project via the existing
 * project store and SEEDS THE AGREED PRICE via the canonical money resolver
 * (`setProjectAgreedPrice` → `gateAmounts` → `resolveGateAmount`) — NO parallel
 * money path — then navigates to the project-scoped delivery intake.
 *
 * Pure logic lives in `lib/paymentToProject.ts` (suggestion shaper + validator);
 * this is the thin UI shell. Pure-white surface; vibrant accent on the CTA.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Btn3 } from './ui';
import { ClientPicker } from './ClientPicker';
import { useToast } from './Toast';
import { useAuth } from '../lib/auth';
import { CORE_TIERS, type CoreTier } from '../lib/engagementTemplate';
import { serviceLabel } from '../lib/intakeCatalog';
import {
  suggestProjectFromPayment,
  validateProjectSuggestion,
  createProjectFromPayment,
  type ProjectSuggestion,
} from '../lib/paymentToProject';

export default function CreateProjectFromPaymentPanel({
  /** Pre-fill the client when known (e.g. from the payment context). */
  initialClient,
  /** Pre-fill the service tier when known (e.g. the quote's tier). */
  initialTier = '',
  /** Pre-fill the agreed amount = the paid amount (auto-suggested). */
  initialAmount = 0,
  /** Pre-fill the currency. */
  initialCurrency = 'INR',
  onClose,
}: {
  initialClient?: { clientId: string; clientNo: string; name: string } | null;
  initialTier?: string;
  initialAmount?: number;
  initialCurrency?: string;
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const actorName = user?.displayName || user?.email || 'Aayatan Veda team';

  const [client, setClient] = useState(initialClient ?? null);
  const [tier, setTier] = useState(initialTier);
  const [amountStr, setAmountStr] = useState(
    initialAmount > 0 ? String(initialAmount) : '',
  );
  const [currency, setCurrency] = useState(initialCurrency);
  const [projectName, setProjectName] = useState('');
  const [busy, setBusy] = useState(false);

  const suggestion: ProjectSuggestion | null = useMemo(() => {
    if (!client) return null;
    return suggestProjectFromPayment({
      client,
      tier,
      amount: Number(amountStr) || 0,
      currency,
      projectName,
    });
  }, [client, tier, amountStr, currency, projectName]);

  const errs = suggestion ? validateProjectSuggestion(suggestion) : ['Pick a client.'];

  async function onConfirm() {
    if (!suggestion || errs.length) {
      toast(errs[0] || 'Complete the form.');
      return;
    }
    setBusy(true);
    try {
      const { projectId, intakeLink } = await createProjectFromPayment(
        suggestion,
        actorName,
      );
      toast(`Project ${projectId} created from this payment.`, { ok: true });
      onClose?.();
      // Open the project-scoped delivery intake (carries scoping answers fwd).
      navigate(intakeLink);
    } catch (e) {
      toast(`Could not create the project: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      title="Create project from this payment"
      note="Pre-filled from the payment — review and confirm. This creates a delivery project and sets its agreed price."
      allowOverflow
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={lbl}>Client</label>
          {initialClient ? (
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
              {initialClient.name} · {initialClient.clientNo}
            </div>
          ) : (
            <ClientPicker
              value={client?.clientId || ''}
              onPick={(c) =>
                setClient({ clientId: c.clientId, clientNo: c.clientNo, name: c.name })
              }
              ariaLabel="Select the client this project is for"
            />
          )}
        </div>

        <div>
          <label style={lbl}>Service</label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            aria-label="Select the service this payment is for"
            style={selStyle}
          >
            <option value="">— choose a service —</option>
            {CORE_TIERS.map((t: CoreTier) => (
              <option key={t} value={t}>
                {serviceLabel(t)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Agreed amount (auto-suggested)</label>
            <input
              style={inputStyle}
              inputMode="decimal"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              placeholder="e.g. 11000"
            />
          </div>
          <div style={{ width: 110 }}>
            <label style={lbl}>Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              aria-label="Currency"
              style={selStyle}
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        <div>
          <label style={lbl}>Project name (optional)</label>
          <input
            style={inputStyle}
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Defaults to “<service> — <client>”"
          />
        </div>

        {errs.length > 0 && client && (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--triage, #DC2626)' }}>
            {errs[0]}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <Btn3 intent="grn" onClick={onConfirm} disabled={busy || !!errs.length}>
            {busy ? 'Creating…' : 'Create project & open intake'}
          </Btn3>
          {onClose && (
            <Btn3 intent="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Btn3>
          )}
        </div>
      </div>
    </Box>
  );
}

const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: 'var(--ink-mute)',
  marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  boxSizing: 'border-box',
};
const selStyle: React.CSSProperties = { ...inputStyle };
